import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { SESSION_DETAIL_FIELDS } from '../../common/utility/activeSessionsMapper';

const MUTED = '#92a9a9';

const PLACEHOLDER_VALUES = new Set(['', '—', '-', 'unknown']);

function hasDisplayValue(value) {
  if (value === null || value === undefined) {
    return false;
  }
  const text = String(value).trim();
  return Boolean(text && !PLACEHOLDER_VALUES.has(text.toLowerCase()));
}

/**
 * Confirmation before signing out all sessions except the current browser.
 */
export default function LogoutOtherSessionsDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
  otherSessions = [],
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const count = otherSessions.length;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isSmallScreen}
      scroll="paper"
      aria-labelledby="logout-other-sessions-title"
      aria-describedby="logout-other-sessions-desc"
      PaperProps={{
        sx: {
          m: isSmallScreen ? 0 : 2,
          borderRadius: isSmallScreen ? 0 : 2,
          bgcolor: 'primaryTheme.shade03',
        },
      }}
    >
      <DialogTitle
        id="logout-other-sessions-title"
        sx={{ fontSize: { mobile: 18, tablet: 20 }, fontWeight: 600, pr: 6 }}
      >
        Log out other sessions?
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: alpha(MUTED, 0.25) }}>
        <Typography
          id="logout-other-sessions-desc"
          sx={{ fontSize: 15, lineHeight: 1.55, color: 'text.secondary', mb: 2 }}
        >
          This will sign you out on{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {count === 1 ? '1 other device or browser' : `${count} other devices or browsers`}
          </Box>
          . You will stay signed in on{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
            this device
          </Box>
          .
        </Typography>

        {count > 0 && (
          <Stack
            spacing={1}
            component="ul"
            sx={{
              m: 0,
              pl: 0,
              listStyle: 'none',
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {otherSessions.map((session) => (
                <Box
                  component="li"
                  key={session.session_id}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    py: 1.25,
                    px: 1.5,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(MUTED, 0.25)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.03),
                  }}
                >
                  <DevicesOtherIcon
                    sx={{ fontSize: 20, color: MUTED, mt: 0.15, flexShrink: 0 }}
                    aria-hidden
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {SESSION_DETAIL_FIELDS.map(([label, key]) => {
                      const value = session[key];
                      if (!hasDisplayValue(value)) {
                        return null;
                      }
                      return (
                        <Typography
                          key={key}
                          sx={{
                            fontSize: 13,
                            color: 'text.secondary',
                            lineHeight: 1.45,
                            mt: 0.25,
                            wordBreak: 'break-word',
                          }}
                        >
                          <Box component="span" sx={{ color: MUTED }}>
                            {label}:
                          </Box>{' '}
                          {value}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
              ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: isSmallScreen ? 'column-reverse' : 'row',
          alignItems: 'stretch',
          gap: 1,
          px: { mobile: 2, tablet: 3 },
          py: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          fullWidth={isSmallScreen}
          sx={{ textTransform: 'none', py: isSmallScreen ? 1.1 : undefined }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading}
          fullWidth={isSmallScreen}
          sx={{
            textTransform: 'none',
            py: isSmallScreen ? 1.1 : undefined,
            minWidth: isSmallScreen ? undefined : 160,
          }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" aria-label="Signing out other sessions" />
          ) : (
            'Log out other sessions'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
